import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme, CssBaseline } from '@mui/material';

const ThemeContext = createContext(null);

/* ──────────────────────────────────────────────────────────────
 * Design tokens - single source of truth for the whole app.
 * v3 "Data Product": clean, light, professional SaaS-fintech.
 * Brand stays Farmgate: navy #0F2744 + green (#1FA254/#22C55E)
 * as a sharp sparing accent. Public site renders light-only;
 * the authed shell keeps its dark palette.
 * ────────────────────────────────────────────────────────────── */

const SYS = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

// Type pairing: Plus Jakarta Sans everywhere (display + UI) with
// Spline Sans Mono for figures/labels (tabular). All-grotesk - no serif.
export const FONT_PAIRINGS = {
  product: { key: 'product', label: 'Plus Jakarta Sans + Spline Sans Mono', display: "'Plus Jakarta Sans'", displayFallback: SYS, heading: "'Plus Jakarta Sans'", body: "'Plus Jakarta Sans'", mono: "'Spline Sans Mono'" },
};
export const DEFAULT_FONTS = FONT_PAIRINGS.product;

// Monospace stack for figures/labels.
export const MONO = `${DEFAULT_FONTS.mono}, ui-monospace, 'SF Mono', Menlo, monospace`;
// Display family (same grotesk, used big and heavy).
export const DISPLAY = `${DEFAULT_FONTS.display}, ${DEFAULT_FONTS.displayFallback}`;

// Reusable text treatments (caller supplies the colour).
export const numericSx = { fontFamily: MONO, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em' };
export const eyebrowSx = { fontFamily: DISPLAY, textTransform: 'uppercase', letterSpacing: '0.13em', fontSize: '0.72rem', fontWeight: 700, lineHeight: 1.6 };
// Big display headlines: heavy Jakarta, tight tracking.
export const displaySx = {
  fontFamily: DISPLAY, fontWeight: 800, letterSpacing: '-0.035em', lineHeight: 1.05,
  fontSize: { xs: '2.2rem', sm: '2.7rem', md: '3.15rem', lg: '3.3rem' },
};
export const displaySmSx = {
  fontFamily: DISPLAY, fontWeight: 800, letterSpacing: '-0.028em', lineHeight: 1.1,
  fontSize: { xs: '1.6rem', md: '2.05rem' },
};

// One radius scale.
export const radius = { sm: 8, control: 10, card: 16, panel: 24, pill: 999 };

const lightPalette = {
  // Clean product neutrals - white canvas, soft grey surfaces, crisp hairlines.
  bg: '#FFFFFF',
  surface: '#F6F8FA',
  surfaceHover: '#EEF1F4',
  card: '#FFFFFF',
  cardBorder: '#E6E9EE',
  cardHover: '#FCFDFE',
  text: '#111827',
  textSecondary: '#5B6472',
  textMuted: '#9AA3AF',
  navBg: 'rgba(255,255,255,0.86)',
  navBorder: '#E6E9EE',
  chartCard: '#FFFFFF',
  chartBorder: '#E6E9EE',
  chartGrid: '#EEF1F4',
  chartAxis: '#9AA3AF',
  tooltipBg: '#0F2744',
  tooltipBorder: '#1E3A5F',
  tooltipText: '#F8FAFC',
  // Brand hero surface (Login brand panel, navy anchors).
  heroBg: '#0F2744',
  heroText: '#F7F9FC',
  // Green is a SIGNAL accent: CTAs, up-deltas, live dot, accent word.
  accent: '#1FA254',
  accentStrong: '#22C55E',
  accentLight: '#7EE0A2',
  accentBg: 'rgba(34,197,94,0.11)',
  navy: '#0F2744',
  navyDeep: '#0A1F38',
  forest: '#14533B',
  pine: '#0B3D2E',
  emerald: '#15803D',
  brass: '#B08D57',
  brassBg: 'rgba(176,141,87,0.12)',
  danger: '#DA3B3B',
  dangerBg: 'rgba(218,59,59,0.10)',
  // Amber = "caution" (naira weakening reads as caution, never as a win).
  warning: '#B45309',
  warningBg: 'rgba(180,83,9,0.11)',
  info: '#1E4D80',
  infoBg: 'rgba(30,77,128,0.08)',
  purple: '#8b5cf6',
  purpleBg: 'rgba(139,92,246,0.08)',
  teal: '#0E7490',
  tealBg: 'rgba(14,116,144,0.08)',
  divider: '#E6E9EE',
  heroGradient: 'linear-gradient(180deg, #FFFFFF 0%, #F6F8FA 100%)',
  brandGradient: 'linear-gradient(150deg, #0F2744 0%, #14533B 100%)',
  brandGradientNavy: 'linear-gradient(150deg, #14533B 0%, #0C2340 120%)',
  ctaGradient: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
  surfaceSheen: 'linear-gradient(180deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0) 40%)',
  accentGlow: 'rgba(34,197,94,0.06)',
  // Soft neutral downward elevation.
  shadowSm: '0 1px 2px rgba(16,24,40,0.05)',
  shadowMd: '0 6px 20px -8px rgba(16,24,40,0.12), 0 2px 6px -3px rgba(16,24,40,0.08)',
  shadowLg: '0 24px 60px -22px rgba(15,39,68,0.22), 0 8px 20px -12px rgba(16,24,40,0.12)',
};

const darkPalette = {
  // Navy obsidian - used by the authed app shell only (public is light-only).
  bg: '#0A1422',
  surface: '#0F1E33',
  surfaceHover: '#16273F',
  card: '#0F1E33',
  cardBorder: '#21344F',
  cardHover: '#16273F',
  text: '#F4F1EA',
  textSecondary: '#AEB9C9',
  textMuted: '#7F8DA3',
  navBg: 'rgba(10,20,34,0.92)',
  navBorder: '#21344F',
  chartCard: '#0F1E33',
  chartBorder: '#21344F',
  chartGrid: 'rgba(255,255,255,0.06)',
  chartAxis: 'rgba(255,255,255,0.4)',
  tooltipBg: '#16273F',
  tooltipBorder: '#284166',
  tooltipText: '#F4F1EA',
  heroBg: '#0A1F38',
  heroText: '#F7F4EE',
  accent: '#22C55E',
  accentStrong: '#22C55E',
  accentLight: '#7EE0A2',
  accentBg: 'rgba(34,197,94,0.14)',
  navy: '#0F2744',
  navyDeep: '#0A1F38',
  forest: '#14533B',
  pine: '#0B3D2E',
  emerald: '#15803D',
  brass: '#C6A36B',
  brassBg: 'rgba(198,163,107,0.14)',
  danger: '#F87171',
  dangerBg: 'rgba(248,113,113,0.14)',
  warning: '#D97706',
  warningBg: 'rgba(217,119,6,0.14)',
  info: '#5B8FD1',
  infoBg: 'rgba(91,143,209,0.14)',
  purple: '#8b5cf6',
  purpleBg: 'rgba(139,92,246,0.12)',
  teal: '#14b8a6',
  tealBg: 'rgba(20,184,166,0.12)',
  divider: '#21344F',
  heroGradient: 'linear-gradient(150deg, #081A2E 0%, #0A1F38 46%, #0C3327 100%)',
  brandGradient: 'linear-gradient(150deg, #0F2744 0%, #14533B 100%)',
  brandGradientNavy: 'linear-gradient(150deg, #14533B 0%, #0C2340 120%)',
  ctaGradient: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
  surfaceSheen: 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0) 40%)',
  accentGlow: 'rgba(34,197,94,0.05)',
  shadowSm: '0 1px 2px rgba(0,0,0,0.40), 0 8px 20px -10px rgba(0,0,0,0.55)',
  shadowMd: '0 2px 6px rgba(0,0,0,0.50), 0 16px 36px -12px rgba(0,0,0,0.65)',
  shadowLg: '0 4px 10px rgba(0,0,0,0.55), 0 28px 60px -18px rgba(0,0,0,0.7)',
};

export function getPalette(mode) {
  return mode === 'dark' ? darkPalette : lightPalette;
}

function buildTypography(fonts) {
  const family = `${fonts.heading}, ${SYS}`;
  const h = (fontSize, fontWeight, letterSpacing, lineHeight = 1.15) => ({ fontFamily: family, fontWeight, fontSize, letterSpacing, lineHeight });
  return {
    fontFamily: `${fonts.body}, ${SYS}`,
    h1: h('3.15rem', 800, '-0.035em', 1.05),
    h2: h('2.05rem', 800, '-0.028em', 1.1),
    h3: h('1.5rem', 700, '-0.022em'),
    h4: h('1.25rem', 700, '-0.018em'),
    h5: h('1.1rem', 700, '-0.012em'),
    h6: h('1rem', 700, '-0.008em'),
    subtitle1: { fontWeight: 600, letterSpacing: '-0.005em' },
    subtitle2: { fontWeight: 600 },
    button: { textTransform: 'none', fontWeight: 600, letterSpacing: 0 },
  };
}

// Build the MUI theme for a given mode + font pairing.
export function createAppTheme(mode, fonts = DEFAULT_FONTS) {
  const palette = getPalette(mode);
  return createTheme({
    palette: {
      mode,
      primary: { main: palette.accent },
      secondary: { main: '#0F2744' },
      background: { default: palette.bg, paper: palette.card },
      text: { primary: palette.text, secondary: palette.textSecondary },
      divider: palette.divider,
    },
    typography: buildTypography(fonts),
    shape: { borderRadius: radius.control },
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: radius.card,
            border: `1px solid ${palette.cardBorder}`,
            boxShadow: palette.shadowSm,
            backgroundImage: 'none',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
          outlined: { borderColor: palette.cardBorder },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: radius.control, textTransform: 'none', fontWeight: 600, transition: 'background-color .2s ease, box-shadow .2s ease, border-color .2s ease' },
          containedPrimary: { color: '#fff', '&:hover': { backgroundColor: '#1C9550', boxShadow: palette.shadowMd } },
          outlined: { borderColor: palette.cardBorder, color: palette.text, '&:hover': { borderColor: palette.textMuted, backgroundColor: palette.surface } },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: { root: { borderRadius: radius.control } },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: radius.sm, fontWeight: 600 },
          filledPrimary: { color: '#fff' },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: { transition: 'background-color .15s ease' },
          hover: { '&:hover': { backgroundColor: palette.surface } },
        },
      },
      MuiToggleButton: {
        styleOverrides: { root: { textTransform: 'none', fontWeight: 600, borderRadius: radius.control } },
      },
      MuiTab: {
        styleOverrides: { root: { textTransform: 'none', fontWeight: 600 } },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: palette.divider },
          head: { fontWeight: 700, color: palette.textMuted, fontSize: '0.72rem', letterSpacing: '0.06em', textTransform: 'uppercase' },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: { borderRadius: radius.pill, backgroundColor: palette.surfaceHover },
        },
      },
    },
  });
}

export function ThemeContextProvider({ children }) {
  const [mode, setMode] = useState(() => {
    try { return localStorage.getItem('ciq-theme') || 'light'; } catch { return 'light'; }
  });

  const toggleMode = useCallback(() => {
    setMode((prev) => {
      const next = prev === 'light' ? 'dark' : 'light';
      try { localStorage.setItem('ciq-theme', next); } catch {}
      return next;
    });
  }, []);

  const palette = getPalette(mode);
  const isDark = mode === 'dark';

  const muiTheme = useMemo(() => createAppTheme(mode), [mode]);

  const value = useMemo(() => ({ mode, toggleMode, palette, isDark }), [mode, toggleMode, palette, isDark]);

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

// Pins a subtree to the LIGHT theme regardless of the global mode.
// The public marketing site is light-only; the authed shell keeps its toggle.
export function ForceLightTheme({ children }) {
  const muiTheme = useMemo(() => createAppTheme('light'), []);
  const value = useMemo(
    () => ({ mode: 'light', toggleMode: () => {}, palette: lightPalette, isDark: false }),
    []
  );
  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={muiTheme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

export function useThemeMode() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    // Fallback for components not inside provider
    return { mode: 'light', toggleMode: () => {}, palette: lightPalette, isDark: false };
  }
  return ctx;
}
